import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { path: '/menu', label: 'MENU' },
    { path: '/events', label: 'SPORTS & EVENTS' },
    { path: '/reservations', label: 'CONTACT' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1a1a1a]/95 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Text Only */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center group"
          >
            <span className="text-[#D4AF37] text-xl mr-2">☘</span>
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#E8E6D9]/90 group-hover:text-[#D4AF37] transition-colors tracking-wide">
              The Shamrock
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`font-['Manrope'] text-sm tracking-[0.15em] transition-colors duration-300 whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-[#D4AF37]'
                    : 'text-[#E8E6D9]/80 hover:text-[#D4AF37]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/reservations"
              data-testid="nav-reserve-btn"
              className="border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 rounded-none font-['Manrope'] text-sm tracking-[0.1em] hover:bg-[#D4AF37] hover:text-[#1a1a1a] transition-all duration-300"
            >
              RESERVE TABLE
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#E8E6D9] hover:text-[#D4AF37] transition-colors"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden bg-[#1a1a1a]/98 backdrop-blur-md border-t border-white/5 py-6"
          >
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`font-['Manrope'] text-base tracking-wider px-4 py-2 transition-colors ${
                    isActive(link.path)
                      ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                      : 'text-[#E8E6D9] hover:text-[#D4AF37] hover:bg-[#D4AF37]/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4 pt-4">
                <Link
                  to="/reservations"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-reserve-btn"
                  className="block w-full text-center border border-[#D4AF37] text-[#D4AF37] px-6 py-3 font-['Manrope'] tracking-wider hover:bg-[#D4AF37] hover:text-[#1a1a1a] transition-all"
                >
                  RESERVE TABLE
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
